import React, { useEffect, useRef } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { useAuth } from "@/lib/auth";
import { pageTransition } from "@/lib/motion";

/** Drives the global --mx / --my CSS custom properties that power the
 * app-wide metallic mouse-sheen on every `.panel-elevated` surface —
 * rAF-throttled, no re-renders, disabled for reduced-motion users. */
function useGlobalMouseGlow() {
    const raf = useRef(null);
    useEffect(() => {
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
        const onMove = (e) => {
            if (raf.current) return;
            raf.current = requestAnimationFrame(() => {
                document.documentElement.style.setProperty("--mx", `${e.clientX}px`);
                document.documentElement.style.setProperty("--my", `${e.clientY}px`);
                raf.current = null;
            });
        };
        window.addEventListener("mousemove", onMove, { passive: true });
        return () => window.removeEventListener("mousemove", onMove);
    }, []);
}

export default function Shell() {
    const { user, loading } = useAuth();
    const location = useLocation();
    useGlobalMouseGlow();
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen text-slate-400 font-mono text-xs uppercase tracking-[0.2em]">
                Initializing ASSEP…
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return (
        // Fixed-height app shell: the sidebar and the content region scroll
        // independently so the topbar never scrolls away and full-height modules
        // (Utsav, Knowledge Graph) can size themselves against the viewport.
        <div className="flex h-screen overflow-hidden">
            <a href="#module-content" className="skip-link" data-testid="skip-to-content">
                Skip to module content
            </a>
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <Topbar />
                <main className="flex-1 min-h-0 overflow-y-auto relative" data-testid="app-scroll-region">
                    <div id="module-content" tabIndex={-1} className="px-6 py-6 max-w-[1920px] w-full mx-auto">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={location.pathname}
                                initial={pageTransition.initial}
                                animate={pageTransition.animate}
                                exit={pageTransition.exit}
                                transition={pageTransition.transition}
                            >
                                <Outlet />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
}
